import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import AuthService from 'services/auth';
import config from 'utils/config';
import tokenManager from 'utils/tokenManager';
import fcmService from 'services/fcm';
import routeStore from 'utils/routeStore';
import { get, isEmpty, compact, debounce } from 'utils/lodash';
import { User } from './types';

interface AuthState {
  user: User | null;
  currentUser: User | null;
  token: string | null;
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthLoading: boolean;
  loadingToken: boolean;
  error: string | null;
  authError: string | null;

  // Actions
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: (credential: string) => Promise<any>;
  loginWithEmailPassword: (opts: { email: string; password: string }) => Promise<any>;
  userLoginWithGoogle: (opts: { idToken: string }) => Promise<any>;
  register: (opts: { email: string; password: string }) => Promise<any>;
  verifyRegistration: (opts: { email: string; otp: string }) => Promise<any>;
  resendOTP: (opts: { email: string }) => Promise<any>;
  forgotPassword: (opts: { email: string }) => Promise<any>;
  resetPassword: (opts: { email: string; otp: string; newPassword: string }) => Promise<any>;
  removeAccount: () => Promise<any>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<any>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  updateUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  setCurrentUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  clearError: () => void;
  generateRedirectUrl: (opts: { path?: string; params?: Record<string, any>; options?: object }) => string;
  getRedirectUrl: () => string;
  resetRedirect: () => void;
  isTokenExpired: (token: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, getState) => ({
        user: null,
        currentUser: null,
        token: null,
        authToken: null,
        isAuthenticated: false,
        isLoading: false,
        isAuthLoading: false,
        loadingToken: false,
        error: null,
        authError: null,

        login: async (email, password) => {
          return getState().loginWithEmailPassword({ email, password });
        },

        loginWithEmailPassword: async ({ email, password }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            const res = await AuthService.loginWithEmailPassword({
              email,
              password,
            });

            if (get(res, 'data.token')) {
              const token = get(res, 'data.token');
              const user = get(res, 'data.user');

              // Save token and user using TokenManager
              if (token) {
                tokenManager.setTokens(token);
                config.set({ token }); // Keep for backward compatibility
              }
              set({
                user,
                currentUser: user,
                token,
                authToken: token,
                isAuthenticated: true,
                isAuthLoading: false,
              });

              // Register FCM token after successful login
              try {
                await fcmService.getToken();
              } catch (error) {
                console.error('Failed to register FCM token after login:', error);
              }

              // Reset redirect after successful login
              debounce(() => {
                routeStore.resetRedirect();
              }, 1000)();
            }
            return res;
          } catch (error: any) {
            set({
              authError: error.message || 'Login failed',
              isAuthLoading: false,
            });
            throw error;
          }
        },

        loginWithGoogle: async (credential) => {
          return getState().userLoginWithGoogle({ idToken: credential });
        },

        userLoginWithGoogle: async ({ idToken }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            const res = await AuthService.userLoginWithGoogle({ idToken });

            if (get(res, 'data.token')) {
              const token = get(res, 'data.token');
              const user = get(res, 'data.user');

              tokenManager.setTokens(token);
              config.set({ token }); // Keep for backward compatibility
              set({
                user,
                currentUser: user,
                token,
                authToken: token,
                isAuthenticated: true,
                isAuthLoading: false,
              });

              debounce(() => {
                routeStore.resetRedirect();
              }, 1000)();
            }
            return res;
          } catch (error: any) {
            set({
              authError: error.message || 'Google login failed',
              isAuthLoading: false,
            });
            throw error;
          }
        },

        register: async ({ email, password }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            const res = await AuthService.register({ email, password });
            return res;
          } catch (error: any) {
            set({
              authError: error.message || 'Registration failed',
              isAuthLoading: false,
            });
            throw error;
          } finally {
            set({ isAuthLoading: false });
          }
        },

        verifyRegistration: async ({ email, otp }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            const res = await AuthService.verifyRegistration({ email, otp });

            if (get(res, 'data.token')) {
              const token = get(res, 'data.token');
              const user = get(res, 'data.user');

              tokenManager.setTokens(token);
              config.set({ token }); // Keep for backward compatibility
              set({
                user,
                currentUser: user,
                token,
                authToken: token,
                isAuthenticated: true,
                isAuthLoading: false,
              });

              debounce(() => {
                routeStore.resetRedirect();
              }, 1000)();
            }
            return res;
          } catch (error: any) {
            set({
              authError: error.message || 'Verification failed',
              isAuthLoading: false,
            });
            throw error;
          } finally {
            set({ isAuthLoading: false });
          }
        },

        resendOTP: async ({ email }) => {
          return await AuthService.resendOTP({ email });
        },

        forgotPassword: async ({ email }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            return await AuthService.forgotPassword({ email });
          } catch (error: any) {
            set({
              authError: error.message || 'Failed to send reset email',
              isAuthLoading: false,
            });
            throw error;
          } finally {
            set({ isAuthLoading: false });
          }
        },

        resetPassword: async ({ email, otp, newPassword }) => {
          set({ isAuthLoading: true, authError: null });
          try {
            const res = await AuthService.resetPassword({ email, otp, newPassword });
            return get(res, 'data');
          } catch (error: any) {
            set({
              authError: error.message || 'Failed to reset password',
              isAuthLoading: false,
            });
            throw error;
          } finally {
            set({ isAuthLoading: false });
          }
        },

        removeAccount: async () => {
          const res = await AuthService.softDeleteAccount();
          if (get(res, 'data')) {
            getState().logout();
          }
          return res;
        },

        logout: async () => {
          console.log('[AuthStore] Logout called');

          // Delete FCM token before logout
          try {
            await fcmService.deleteToken();
          } catch (error) {
            console.error('Failed to delete FCM token during logout:', error);
          }

          // Clear tokens using TokenManager (this will also notify subscribers)
          tokenManager.clearTokens();
          config.set({ token: '' }); // Keep for backward compatibility

          // Clear state
          set({
            user: null,
            currentUser: null,
            token: null,
            authToken: '',
            isAuthenticated: false,
            error: null,
            authError: null,
          });

          // CRITICAL: Clear persisted storage to prevent rehydration of old token
          // This prevents redirect loop where old token is restored from localStorage
          if (typeof window !== 'undefined') {
            console.log('[AuthStore] Clearing persisted storage');
            try {
              localStorage.removeItem('auth-storage');
            } catch (error) {
              console.error('[AuthStore] Failed to clear persisted storage:', error);
            }
          }

          console.log('[AuthStore] State cleared, redirecting to login');

          // Redirect to login page immediately
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        },

        refreshToken: async () => {
          const state = getState();
          const currentToken = state.authToken || state.token;
          if (!currentToken || !tokenManager.isTokenExpired(currentToken)) return null;

          try {
            const newToken = await tokenManager.refreshAccessToken();
            if (newToken) {
              config.set({ token: newToken }); // Keep for backward compatibility
              set({ token: newToken, authToken: newToken });
              return { data: { refreshedToken: newToken } };
            } else {
              state.logout();
              return null;
            }
          } catch (error) {
            // If refresh fails, logout
            state.logout();
            return null;
          }
        },

        updateUser: async () => {
          try {
            const res = await AuthService.me();
            const user = get(res, 'data');
            if (!isEmpty(user)) {
              set({
                currentUser: user,
                user: user,
              });
            }
          } catch (error) {

          }
        },

        updateProfile: async (data) => {
          set({ isLoading: true, error: null });
          try {
            // TODO: Implement when updateProfile is available in AuthService
            // const response = await AuthService.updateProfile(data);
            const currentUser = getState().user;
            if (!currentUser) throw new Error('User not found');
            const response = { data: { ...currentUser, ...data } as User }; // Mock for now
            if (response?.data) {
              set({
                user: response.data,
                currentUser: response.data,
                isLoading: false,
              });
            }
          } catch (error: any) {
            set({
              error: error.message || 'Update profile failed',
              isLoading: false,
            });
            throw error;
          }
        },

        setUser: (user) => {
          set({ user, currentUser: user, isAuthenticated: !!user });
        },

        setCurrentUser: (user) => {
          set({ currentUser: user, user });
        },

        setToken: (token) => {
          if (token) {
            tokenManager.setTokens(token);
            config.set({ token }); // Keep for backward compatibility
          }
          set({ token, authToken: token, isAuthenticated: !!token });
        },

        generateRedirectUrl: ({ path = '/', params = { pathname: '/' }, options }) => {
          const { pathname, ..._params } = params;
          routeStore.setRedirect({ pathname, params: _params, options });
          return path;
        },

        getRedirectUrl: () => {
          const { pathname } = routeStore.getRedirect();
          const search = '';
          // if (params) {
          //   search = `${qs.stringify(params)}`;
          //   search = search ? `?${search}` : '';
          // }
          return compact([pathname, search]).join('/');
        },

        resetRedirect: () => {
          routeStore.resetRedirect();
        },

        isTokenExpired: (token: string) => {
          if (!token) return true;
          // Skip token validation during SSR
          if (typeof window === 'undefined') return false;
          // Use TokenManager for token expiry check
          return tokenManager.isTokenExpired(token);
        },

        clearError: () => {
          set({ error: null, authError: null });
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          user: state.user,
          currentUser: state.currentUser,
          token: state.token,
          authToken: state.authToken,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state) => {
          console.log('[AuthStore] Rehydrating storage', {
            hasState: !!state,
            hasToken: !!state?.token,
          });

          // Validate token before restoring to prevent redirect loop
          if (state?.token) {
            // Check if token is expired
            if (tokenManager.isTokenExpired(state.token)) {
              console.warn('[AuthStore] Stored token is expired, clearing storage');
              // Clear the expired token from localStorage
              if (typeof window !== 'undefined') {
                try {
                  localStorage.removeItem('auth-storage');
                } catch (error) {
                  console.error('[AuthStore] Failed to clear expired token from storage:', error);
                }
              }
              // Don't restore the expired token
              return;
            }

            // Token is valid, restore it
            console.log('[AuthStore] Restoring valid token');
            tokenManager.setTokens(state.token);
            config.set({ token: state.token }); // Keep for backward compatibility
          }
        },
      }
    ),
    { name: 'auth-store' }
  )
);